import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { getEmailStatus, sendMondayRecap, getReminderSettings, updateReminderSettings, getEmailSuppressions, removeEmailSuppression, getEmailSettings, updateEmailSetting } from "../../lib/api";

function EmailNotificationsCard() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await getEmailSettings();
      setTypes(r.data?.types || []);
    } catch {
      toast.error("Failed to load email notification settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (t, next) => {
    setSavingKey(t.key);
    // Optimistic update.
    setTypes((prev) => prev.map((x) => (x.key === t.key ? { ...x, enabled: next } : x)));
    try {
      await updateEmailSetting(t.key, next);
      toast.success(`${t.label} ${next ? "on" : "off"}`);
    } catch (e) {
      setTypes((prev) => prev.map((x) => (x.key === t.key ? { ...x, enabled: !next } : x)));
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingKey("");
    }
  };

  const categories = [...new Set(types.map((t) => t.category))];

  return (
    <Card data-testid="email-notifications-card">
      <CardHeader>
        <CardTitle>Email Notifications</CardTitle>
        <CardDescription>
          Every automated email SMARTIS sends is listed below. Turn any type off to stop it being sent — changes take effect immediately. In-app and push notifications are not affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          categories.map((cat) => (
            <div key={cat} data-testid={`email-category-${cat}`}>
              <h4 className="text-sm font-semibold text-[#00305E] uppercase tracking-wide mb-2">{cat}</h4>
              <div className="divide-y border rounded-lg">
                {types.filter((t) => t.category === cat).map((t) => (
                  <div key={t.key} className="flex items-start justify-between gap-4 p-4" data-testid={`email-toggle-row-${t.key}`}>
                    <div className="space-y-1 min-w-0">
                      <Label className="font-medium block">{t.label}</Label>
                      <p className="text-sm text-muted-foreground">{t.description}</p>
                      <p className="text-xs text-muted-foreground"><span className="font-medium">Recipients:</span> {t.audience}</p>
                    </div>
                    <Switch
                      checked={t.enabled}
                      disabled={savingKey === t.key}
                      onCheckedChange={(v) => toggle(t, v)}
                      data-testid={`email-toggle-${t.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function SuppressedEmailsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await getEmailSuppressions();
      setItems(r.data?.suppressions || []);
    } catch {
      toast.error("Failed to load suppressed emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clear = async (row) => {
    const target = row.email || row.id;
    if (!window.confirm(`Clear the suppression for ${target}? Fix the reason it bounced first, or it will be re-suppressed on the next send.`)) return;
    setClearing(target);
    try {
      await removeEmailSuppression(target);
      toast.success(`Cleared ${target}. You can re-invite this address now.`);
      setItems((prev) => prev.filter((x) => x.id !== row.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear suppression");
    } finally {
      setClearing("");
    }
  };

  return (
    <Card data-testid="suppressed-emails-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Suppressed Email Addresses</CardTitle>
            <CardDescription>
              Resend stops sending to addresses that hard-bounced or reported spam. Clear one here (after fixing the address) so it can be re-invited.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="suppressions-refresh-btn">
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="suppressions-empty">No suppressed addresses. All good.</p>
        ) : (
          <div className="divide-y border rounded-md" data-testid="suppressions-list">
            {items.map((row) => (
              <div key={row.id} className="flex items-center gap-3 p-3" data-testid={`suppression-row-${row.email}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{row.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.origin || "suppressed"}
                    {row.created_at ? ` · since ${String(row.created_at).slice(0, 10)}` : ""}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => clear(row)}
                  disabled={clearing === (row.email || row.id)}
                  data-testid={`suppression-clear-${row.email}`}
                >
                  {clearing === (row.email || row.id) ? "Clearing…" : "Clear"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceRemindersCard() {
  const [enabled, setEnabled] = useState(true);
  const [time, setTime] = useState("09:30");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getReminderSettings()
      .then((r) => {
        setEnabled(r.data.attendance_reminders_enabled);
        setTime(r.data.attendance_reminder_time || "09:30");
      })
      .catch(() => toast.error("Failed to load attendance reminder settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateReminderSettings({
        attendance_reminders_enabled: enabled,
        attendance_reminder_time: time,
      });
      setEnabled(res.data.attendance_reminders_enabled);
      setTime(res.data.attendance_reminder_time);
      toast.success("Attendance reminder settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="attendance-reminders-card">
      <CardHeader>
        <CardTitle>Attendance Reminders</CardTitle>
        <CardDescription>
          When on, staff are alerted about students who are expected but not yet marked present. The check runs once each school day at the time you set below (school-local / Eastern time).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Send attendance reminders to staff</Label>
                <p className="text-sm text-muted-foreground">Notify the assigned clinician for each student about missing check-ins.</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="attendance-reminders-toggle" />
            </div>
            <div className="flex items-end gap-3">
              <div className="w-40">
                <Label className="text-xs text-muted-foreground">Send time</Label>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={!enabled}
                  data-testid="attendance-reminder-time-input"
                />
              </div>
              <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="attendance-reminders-save-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function NotificationsEmailTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getEmailStatus()
      .then((r) => setStatus(r.data))
      .catch(() => toast.error("Failed to load email status"))
      .finally(() => setLoading(false));
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await sendMondayRecap();
      const sent = res.data?.sent ?? 0;
      if (sent > 0) toast.success(`Recap sent to ${sent} recipient${sent === 1 ? "" : "s"}`);
      else if (res.data?.skipped === "no_admins") toast.warning("No admin/director staff with an email to send to");
      else if (res.data?.skipped === "no_data") toast.info("No risk-assessment data to recap yet");
      else toast.info("Recap triggered");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send recap");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-email-tab">
      <EmailNotificationsCard />
      <AttendanceRemindersCard />
      <SuppressedEmailsCard />
      <Card>
        <CardHeader>
          <CardTitle>Email &amp; Transactional Messages</CardTitle>
          <CardDescription>
            SMARTIS uses Resend to send the Monday risk recap, progress-report emails, and password resets.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : status ? (
            <>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Email service:</span>
                {status.configured ? (
                  <Badge className="bg-green-600" data-testid="email-status-configured">Connected</Badge>
                ) : status.key_valid ? (
                  <Badge variant="outline" className="border-amber-500 text-amber-700" data-testid="email-status-domain-unverified">Domain not verified</Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-500 text-amber-700" data-testid="email-status-not-configured">Not configured</Badge>
                )}
              </div>
              {!status.key_valid && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Add a valid <code>RESEND_API_KEY</code> (starts with <code>re_</code>) in the deployment settings to enable outgoing email.
                </p>
              )}
              {status.key_valid && !status.domain_verified && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  The API key is valid, but the sender domain <span className="font-mono">{status.sender_domain}</span> is
                  {" "}<strong>{status.domain_status === "not_added" ? "not added to this Resend account" : `not verified (status: ${status.domain_status || "unknown"})`}</strong>.
                  {" "}Resend will reject sends until you verify it in the Resend dashboard (Domains → add DNS records).
                </p>
              )}
              <div className="text-sm">
                <span className="text-muted-foreground">Sender address: </span>
                <span className="font-mono">{status.sender_email}</span>
              </div>
              <div>
                <div className="text-sm font-semibold mb-1">Monday recap recipients</div>
                {status.recap_recipients?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {status.recap_recipients.map((r) => (
                      <Badge key={r.email} variant="secondary" className="text-xs">{r.name} · {r.email}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No admin/director staff have an email yet. Recipients are staff whose position includes Admin, Director, Principal, or Head of School.
                  </p>
                )}
              </div>
              <div className="pt-2 border-t">
                <Button onClick={sendTest} disabled={sending || !status.configured} variant="outline" data-testid="send-test-recap-btn">
                  {sending ? "Sending…" : "Send recap now"}
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
